import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  Box,
  Typography,
  IconButton,
  Button,
  Grid,
  Tooltip,
  Divider,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";

export default function CompatibilitySection({
  appsLabels,     // mantido para compatibilidade futura (não usado aqui)
  appsLoading,    // idem
  formData,
  setFormData,
  handleText,
}) {
  const { t } = useTranslation("products");
  // Aplicações vêm da seção "Aplicação" (livre e dinâmica)
  const apps = Array.isArray(formData.aplicacao)
    ? formData.aplicacao.map((s) => String(s || "").trim()).filter(Boolean)
    : [];

  // Mapa: { [aplicacao]: Array<{ family, years?, engine?, notes? }> }
  const models = formData.compat_models || {};

  const setModels = (updater) => {
    setFormData((prev) => ({
      ...prev,
      compat_models: updater(prev.compat_models || {}),
    }));
  };

  const addModel = (app) => {
    setModels((prev) => {
      const base = Array.isArray(prev[app]) ? [...prev[app]] : [];
      base.push({ family: "", years: "", engine: "", notes: "" });
      return { ...prev, [app]: base };
    });
  };

  const removeModel = (app, idx) => {
    setModels((prev) => {
      const base = Array.isArray(prev[app]) ? [...prev[app]] : [];
      base.splice(idx, 1);
      return { ...prev, [app]: base };
    });
  };

  const moveModel = (app, idx, dir) => {
    setModels((prev) => {
      const base = Array.isArray(prev[app]) ? [...prev[app]] : [];
      const to = idx + dir;
      if (to < 0 || to >= base.length) return prev;
      const [it] = base.splice(idx, 1);
      base.splice(to, 0, it);
      return { ...prev, [app]: base };
    });
  };

  const setField = (app, idx, field, value) => {
    setModels((prev) => {
      const base = Array.isArray(prev[app]) ? [...prev[app]] : [];
      base[idx] = { ...base[idx], [field]: value };
      return { ...prev, [app]: base };
    });
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardHeader
        title={t("form.compatibilityTitle")}
        sx={{ pb: 0.5, "& .MuiCardHeader-title": { fontSize: 16, fontWeight: 600 } }}
      />
      <CardContent sx={{ pt: 2, display: "grid", gap: 2 }}>
        {/* Alerta livre (texto) */}
        <TextField
          label={t("form.compatibilityNoticeLabel")}
          name="compat_alerta"
          value={formData.compat_alerta || ""}
          onChange={handleText}
          fullWidth
          multiline
          minRows={2}
          placeholder={t("form.compatibilityNoticePlaceholder")}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
          <InfoOutlinedIcon fontSize="small" />
          <Typography variant="body2">
            {t("form.compatHint")}
          </Typography>
        </Box>

        <Divider />

        {!apps.length && (
          <Typography variant="body2" color="text.secondary">
            {t("form.compatNoApps")}
          </Typography>
        )}

        {apps.map((app) => {
          const list = Array.isArray(models[app]) ? models[app] : [];
          return (
            <Box key={app} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  bgcolor: "background.default",
                }}
              >
                <Typography fontWeight={700}>{app}</Typography>
                <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => addModel(app)}>
                  {t("form.compatAddModel")}
                </Button>
              </Box>

              <Box sx={{ p: 2, display: "grid", gap: 1.5 }}>
                {list.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t("form.compatNoModelsForApp", { app })}
                  </Typography>
                ) : (
                  list.map((m, idx) => (
                    <Box
                      key={`${app}-${idx}`}
                      sx={{
                        p: 1,
                        border: "1px dashed",
                        borderColor: "divider",
                        borderRadius: 1,
                        display: "grid",
                        gap: 1,
                      }}
                    >
                      <Grid container spacing={1}>
                        <Grid item xs={12} md={3}>
                          <TextField
                            label={t("form.compatFamilyModel")}
                            value={m.family || ""}
                            onChange={(e) => setField(app, idx, "family", e.target.value)}
                            required
                            fullWidth
                            size="small"
                            placeholder={t("form.compatFamilyPlaceholder")}
                          />
                        </Grid>
                        <Grid item xs={12} md={2.5}>
                          <TextField
                            label={t("form.compatYearsLabel")}
                            value={m.years || ""}
                            onChange={(e) => setField(app, idx, "years", e.target.value)}
                            fullWidth
                            size="small"
                            placeholder={t("form.compatYearsPlaceholder")}
                          />
                        </Grid>
                        <Grid item xs={12} md={2.5}>
                          <TextField
                            label={t("form.compatEngine")}
                            value={m.engine || ""}
                            onChange={(e) => setField(app, idx, "engine", e.target.value)}
                            fullWidth
                            size="small"
                            placeholder={t("form.compatEnginePlaceholder")}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            label={t("form.compatNotes")}
                            value={m.notes || ""}
                            onChange={(e) => setField(app, idx, "notes", e.target.value)}
                            fullWidth
                            size="small"
                            placeholder={t("form.compatNotesPlaceholder")}
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          md={1}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: 0.5,
                          }}
                        >
                          <Tooltip title={t("form.compatMoveUp")}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => moveModel(app, idx, -1)}
                                disabled={idx === 0}
                              >
                                <ArrowUpwardRoundedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={t("form.compatMoveDown")}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => moveModel(app, idx, +1)}
                                disabled={idx === list.length - 1}
                              >
                                <ArrowDownwardRoundedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={t("form.warrantyRemoveTooltip")}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeModel(app, idx)}
                            >
                              <DeleteOutlineRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          );
        })}
      </CardContent>
    </Card>
  );
}
